import { useQuery } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import { useWorkspaceStore } from "@/stores/workspaceStore";

export type OrgSystemBudget = {
  /** The preset label, or null for an organization that never had one assigned. */
  plan: "SOLO" | "TEAM" | "MAX" | null;
  limitUsd: number;
  topUpUsd: number;
  spentUsd: number;
  remainingUsd: number;
  /** Above zero only when client-review activity pushed past the ceiling. */
  overspendUsd: number;
  periodStart: string;
  periodEnd: string;
  exhausted: boolean;
  seats: { members: number; pendingInvites: number; maxSeats: number | null };
};

/**
 * The organization's system allowance — the pot that pays for work nobody
 * clicked: overnight planning, and whatever a client's decision in
 * the review portal sets off.
 *
 * Read-only on purpose. Only a platform admin can top this up, so there is
 * nothing here to mutate; the banner tells people who to ask.
 *
 * No polling. It changes on a nightly cadence, and focus refetch (on globally)
 * covers coming back to the tab after an admin has topped up.
 */
export function useOrgSystemBudget() {
  const organizationId = useWorkspaceStore((s) => s.selectedOrganizationId);

  return useQuery({
    queryKey: ["orgSystemBudget", organizationId],
    queryFn: async () => {
      const res = await axiosInstance.get<OrgSystemBudget>(
        `/organization/${organizationId}/system-budget`,
      );
      return res.data;
    },
    enabled: Boolean(organizationId),
    staleTime: 60_000,
  });
}
